import React, { forwardRef, useEffect, useState } from 'react';

import { cn } from '@/lib/utils';

import { Input } from '../../../../../components/ui/input';

export interface TimeInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type' | 'onChange' | 'value'> {
  value?: string;
  onChange?: (value: string) => void;
}

/**
 * TimeInput component for 24-hour time format (HH:MM)
 * Features:
 * - Auto-formats input as user types
 * - Prevents invalid characters
 * - Validates hours (00-23) and minutes (00-59)
 * - Auto-adds colon separator
 */
export const TimeInput = forwardRef<HTMLInputElement, TimeInputProps>(
  ({ value = '', onChange, className, ...props }, ref) => {
    const [displayValue, setDisplayValue] = useState(value);

    useEffect(() => {
      setDisplayValue(value);
    }, [value]);

    const formatTimeValue = (input: string): string => {
      // Remove all non-digit characters
      const digits = input.replace(/\D/g, '');

      if (digits.length === 0) return '';
      if (digits.length <= 2) {
        // Just hours
        const hours = parseInt(digits, 10);
        if (hours > 23) return '23';
        return digits;
      }

      // Hours and minutes
      let hours = digits.slice(0, 2);
      let minutes = digits.slice(2, 4);

      // Validate hours
      if (parseInt(hours, 10) > 23) hours = '23';

      // Validate minutes
      if (minutes && parseInt(minutes, 10) > 59) minutes = '59';

      return minutes ? `${hours}:${minutes}` : hours;
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const input = e.target.value;
      const formatted = formatTimeValue(input);

      setDisplayValue(formatted);

      // Only call onChange with valid complete time (HH:MM)
      if (formatted.includes(':') && formatted.length === 5) {
        onChange?.(formatted);
      } else if (formatted.length === 0) {
        onChange?.('');
      }
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      const current = e.target.value;

      // Auto-complete partial time on blur
      if (current && !current.includes(':')) {
        if (current.length === 1) {
          // Single digit hour: "9" -> "09:00"
          const formatted = `0${current}:00`;
          setDisplayValue(formatted);
          onChange?.(formatted);
        } else if (current.length === 2) {
          // Two digit hour: "09" -> "09:00"
          const formatted = `${current}:00`;
          setDisplayValue(formatted);
          onChange?.(formatted);
        }
      } else if (current && current.includes(':') && current.length < 5) {
        // Incomplete time: "09:" or "09:5" -> "09:00" or "09:50"
        const [hours, minutes = ''] = current.split(':');
        const paddedMinutes = minutes.padEnd(2, '0');
        const formatted = `${hours}:${paddedMinutes}`;
        setDisplayValue(formatted);
        onChange?.(formatted);
      }

      props.onBlur?.(e);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      // Allow: backspace, delete, tab, escape, enter
      if (
        ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter'].includes(e.key) ||
        // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
        (e.ctrlKey && ['a', 'c', 'v', 'x'].includes(e.key.toLowerCase())) ||
        // Allow: home, end, left, right
        ['Home', 'End', 'ArrowLeft', 'ArrowRight'].includes(e.key)
      ) {
        return;
      }

      // Prevent non-digit input
      if (!/^\d$/.test(e.key)) {
        e.preventDefault();
      }

      props.onKeyDown?.(e);
    };

    return (
      <Input
        ref={ref}
        type="text"
        inputMode="numeric"
        value={displayValue}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        placeholder="00:00"
        maxLength={5}
        className={cn('font-mono', className)}
        {...props}
      />
    );
  }
);

TimeInput.displayName = 'TimeInput';
